import { RECUR_PERIOD_UNIT } from "./constants";
import { SK_MONTHS_NOM } from "./date-sk";
import { session } from "./session";

// Rodovo správny „zapojený" štítok: m → Zapojený · z → Zapojená · x (iné) → Si tam!
// si:true → „Si zapojený/á" (x ostáva „Si tam!"); lower:true → malé začiatočné písmeno.
export function joinedLabel(opts: { si?: boolean; lower?: boolean } = {}): string {
  const g = session.gender;
  if (g === "x") return opts.lower ? "si tam!" : "Si tam!";
  const w = g === "m" ? "zapojený" : "zapojená";
  const base = opts.si ? `si ${w}` : w;
  return opts.lower ? base : base.charAt(0).toUpperCase() + base.slice(1);
}

export const OFFSETS: { m: number; l: string }[] = [
  { m: 1440, l: "24 h" }, { m: 120, l: "2 h" }, { m: 60, l: "1 h" }, { m: 0, l: "v daný čas" },
];
export const offLabel = (m: number): string =>
  (OFFSETS.find((o) => o.m === m) || { l: m + " min" }).l;
export const todayKey = (): string => new Date().toISOString().slice(0, 10);
export const fmtShortDate = (k: string): string => {
  const p = (k || "").split("-");
  return p.length === 3 ? `${+p[2]}.${+p[1]}.` : k;
};
// Relative "time ago" for a server timestamp ('YYYY-MM-DD HH:MM:SS', stored in UTC).
export const relTime = (s: string, now: number = Date.now()): string => {
  if (!s) return "";
  const t = new Date(s.replace(" ", "T") + (s.includes("Z") ? "" : "Z")).getTime();
  if (isNaN(t)) return "";
  const min = Math.floor((now - t) / 60000);
  if (min < 1) return "teraz";
  if (min < 60) return `pred ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `pred ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `pred ${d} d`;
  return fmtShortDate(s.slice(0, 10));
};
// Frekvenčný label návyku (zdieľaný medzi detailom, riadkom Dnes a inde) — jedno miesto.
export interface FreqLike {
  recur_mode?: string | null;
  boost?: boolean | number | null;
  week_target?: number | null;
  recur_period?: string | null;
  recur_n?: number | null;
  recur_days?: string | null;
  per_day?: number | null;
}
export const freqLabel = (t: FreqLike): string => {
  const m = t.recur_mode || "daily";
  if (t.boost) return "Len dnes";
  if (m === "weekly_count") return `${t.week_target || 0}× týžd.`;
  if (m === "period") return `${t.week_target || 0}× ${RECUR_PERIOD_UNIT[t.recur_period as string] || "obdobie"}`;
  if (m === "interval") return `Každý ${t.recur_n}. deň`;
  if (m === "weekdays" && t.recur_days) return t.recur_days.split(",").map((n) => WEEKDAYS_SK[Number(n) - 1]).join("·");
  if ((t.per_day || 1) > 1) return `${t.per_day}× denne`;
  return "Denne";
};
export const pad2 = (n: number): string => String(n).padStart(2, "0");
export const ymd = (y: number, m: number, d: number): string => `${y}-${pad2(m + 1)}-${pad2(d)}`;
export const WEEKDAYS_SK: string[] = ["Po", "Ut", "St", "Št", "Pi", "So", "Ne"];
// ── chat message timestamps ──────────────────────────────────────────────
// Chat messages store `created_at` as 'YYYY-MM-DD HH:MM:SS' in the app's LOCAL timezone
// (Europe/Bratislava — app.py inserts `datetime.now(TZ)` for every chat message: text, poke, AI).
// The HH:MM is therefore already local wall-clock, so we slice it directly — no UTC 'Z' offset
// (unlike `relTime`, which is for UTC `datetime('now')` rows such as notifications).
export const chatTime = (at: string): string => {
  const t = (at || "").slice(11, 16);
  return /^\d{2}:\d{2}$/.test(t) ? t : "";
};
// Day-separator heading between chat days. `key` = message calendar day 'YYYY-MM-DD',
// `today` = todayKey(). "Dnes" / "Včera" / "12. júl" (this year) / "12. júl 2025" (older).
export const chatDayHeading = (key: string, today: string = todayKey()): string => {
  if (!key) return "";
  if (key === today) return "Dnes";
  const y = new Date(today + "T00:00:00");
  y.setDate(y.getDate() - 1);
  if (key === ymd(y.getFullYear(), y.getMonth(), y.getDate())) return "Včera";
  const p = key.split("-");
  if (p.length !== 3) return key;
  const day = +p[2], mon = +p[1] - 1, yr = +p[0];
  const name = SK_MONTHS_NOM[mon] || "";
  return yr === +today.slice(0, 4) ? `${day}. ${name}` : `${day}. ${name} ${yr}`;
};
// v2 — stopky v súťažiach: výsledok sa ukladá v sekundách, zobrazuje ako čas.
// fmtDur(275) → "4:35" · fmtDur(5025) → "1:23:45" · zlomky sekúnd sa zaokrúhľujú.
export const fmtDur = (sec: number | null | undefined): string => {
  if (sec == null || isNaN(Number(sec))) return "–";
  const s = Math.max(0, Math.round(Number(sec)));  // záporný čas neexistuje — nekresli "-2:-30"
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
  return h > 0 ? `${h}:${pad2(m)}:${pad2(r)}` : `${m}:${pad2(r)}`;
};
// parseDur("1:23:45") → 5025 · "4:35" → 275 · "90" → 90 (plain seconds) · záporné/neúplné → NaN
export const parseDur = (v: string): number => {
  const t = (v || "").trim();
  if (!t) return NaN;
  if (!t.includes(":")) {
    const n = Number(t.replace(",", "."));
    return n < 0 ? NaN : n;
  }
  const parts = t.split(":").map((x) => (x.trim() === "" ? NaN : Number(x)));
  if (parts.some((x) => isNaN(x) || x < 0)) return NaN;
  return parts.reduce((acc, x) => acc * 60 + x, 0);
};
